import { Link, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sprout, TestTube, Sparkles, Leaf, TrendingUp, Droplets, CloudSun, ShieldCheck, ArrowRight } from "lucide-react";

const heroImg = "https://images.unsplash.com/photo-1569880153113-76e33fc52d5f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxmYXJtZXIlMjBpbnNwZWN0aW5nJTIwc29pbCUyMGFncmljdWx0dXJlJTIwZmllbGQlMjB0ZXh0dXJlfGVufDB8fHx8MTc4OTE5MTg4xfDA&ixlib=rb-4.1.0&q=85";
const soilImg = "https://images.unsplash.com/photo-1613036582025-ba1d4ccb3226?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxyaWNoJTIwc29pbCUyMGFncmljdWx0dXJlJTIwZmllbGQlMjB0ZXh0dXJlfGVufDB8fHx8MTc4OTE5MTg4OHww&ixlib=rb-4.1.0&q=85";

const features = [
  { icon: TestTube, title: "Soil Diagnostic", desc: "Enter pH, texture, moisture and N-P-K to get an instant soil health score." },
  { icon: Sparkles, title: "AI Soil Story", desc: "Describe your land in plain words. Our AI figures out the rest." },
  { icon: Leaf, title: "Crop Matching", desc: "Get ranked crops that thrive in your exact ground conditions." },
  { icon: ShieldCheck, title: "Treatments & Fertilisers", desc: "Step-by-step plans to fix and improve your soil." },
  { icon: CloudSun, title: "Weather Advice", desc: "Daily, weather-aware guidance for your farm." },
  { icon: TrendingUp, title: "Saved Fields", desc: "Track every field and revisit past analyses anytime." },
];

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  useEffect(() => { if (user) navigate("/app"); }, [user, navigate]);

  return (
    <div className="min-h-screen bg-[#FAF9F6] text-stone-900">
      {/* Nav */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-[#FAF9F6]/80 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-[#2D6A4F] flex items-center justify-center">
              <Sprout className="h-5 w-5 text-white" />
            </div>
            <span className="font-display font-extrabold text-lg tracking-tight">AgroTerra</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" data-testid="landing-login-btn" onClick={() => navigate("/login")} className="text-stone-700">Log in</Button>
            <Button data-testid="landing-signup-btn" onClick={() => navigate("/register")} className="bg-[#2D6A4F] hover:bg-[#143D29] text-white rounded-full px-5">
              Get started
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-20 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#A7F3D0]/50 border border-[#52B788]/40 px-4 py-1.5 text-sm font-semibold text-[#143D29] mb-6">
            <Sparkles className="h-4 w-4" /> AI-powered soil intelligence
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-emerald-950 leading-tight">
            Know your ground.<br />Grow the right crop.
          </h1>
          <p className="mt-6 text-base leading-relaxed text-stone-600 max-w-lg">
            AgroTerra studies your soil — pH, texture, moisture and nutrients — then tells you exactly what to plant, how to treat the land, and what products fit best.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button data-testid="hero-cta-btn" onClick={() => navigate("/register")} className="bg-[#D97706] hover:bg-[#78350F] text-white rounded-full px-7 h-12 text-base">
              Analyse my soil <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
            <Button variant="outline" onClick={() => navigate("/login")} className="rounded-full px-7 h-12 text-base border-stone-300">
              I have an account
            </Button>
          </div>
        </div>
        <div className="lg:col-span-6 relative">
          <div className="rounded-3xl overflow-hidden shadow-2xl shadow-emerald-950/20 border-4 border-white">
            <img src={heroImg} alt="Farmer inspecting soil" className="w-full h-[420px] object-cover" />
          </div>
          <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-4 flex items-center gap-3 border border-stone-100">
            <div className="h-11 w-11 rounded-xl bg-[#52B788] flex items-center justify-center">
              <Droplets className="h-5 w-5 text-emerald-950" />
            </div>
            <div>
              <div className="font-mono-tech font-bold text-lg text-emerald-950">92<span className="text-sm text-stone-400">/100</span></div>
              <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Soil Health</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-24">
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-emerald-900 mb-3">Everything a farmer needs</h2>
        <p className="text-stone-600 mb-10 max-w-xl">From the dirt under your boots to the harvest in your barn.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {features.map((f) => (
            <div key={f.title} className="group bg-white rounded-2xl p-6 border border-stone-200 hover:border-[#52B788] hover:shadow-lg transition-all">
              <div className="h-12 w-12 rounded-xl bg-[#EAF6EF] flex items-center justify-center mb-4 group-hover:bg-[#52B788] transition-colors">
                <f.icon className="h-6 w-6 text-[#2D6A4F] group-hover:text-emerald-950 transition-colors" />
              </div>
              <h3 className="font-display text-xl font-semibold text-emerald-950 mb-2">{f.title}</h3>
              <p className="text-stone-600 text-sm leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA band */}
      <section className="bg-[#143D29] text-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight mb-4">Ready to read your land?</h2>
            <p className="text-emerald-100/80 mb-6 max-w-md">Create a free account and get your first soil analysis in under a minute.</p>
            <Button data-testid="cta-band-btn" onClick={() => navigate("/register")} className="bg-[#52B788] hover:bg-[#40916C] text-emerald-950 rounded-full px-7 h-12 text-base font-semibold">
              Start free
            </Button>
          </div>
          <div className="rounded-3xl overflow-hidden border-4 border-emerald-800/50">
            <img src={soilImg} alt="Rich soil" className="w-full h-64 object-cover" />
          </div>
        </div>
      </section>

      <footer className="bg-[#0F1F17] text-emerald-200/60 text-sm py-8 text-center">
        © {new Date().getFullYear()} AgroTerra · AI Soil & Crop Intelligence
      </footer>
    </div>
  );
}
